import React from 'react';
import dayjs from 'dayjs';
import paper from '../ReportPaper.module.css';
import styles from './cranialBody.module.css';
import ClinicalReportPhotos from '../../ClinicalReportPhotos';
import CranialAsymmetryChart from '../../../../pages/appointment/components/cranialAsymmetry/CranialAsymmetryChart';
import {
  buildCranialAsymmetryReportData,
  buildMeasurementChanges,
  buildTreatmentSummary,
  formatCranialAsymmetryHistory,
} from '../../../../pages/appointment/components/cranialAsymmetry/cranialAsymmetryReportAdapter';

// Cores das etiquetas de severidade (mesma escala do ClinicalReportTemplate)
const SEVERITY_STYLE = {
  Normal: { color: '#2E7D32', background: '#E8F5E9', borderColor: '#C8E6C9' },
  Leve: { color: '#F9A825', background: '#FFFDE7', borderColor: '#FFF9C4' },
  Moderada: { color: '#E65100', background: '#FFF3E0', borderColor: '#FFE0B2' },
  Severa: { color: '#C62828', background: '#FFEBEE', borderColor: '#FFCDD2' },
  'Muito severa': { color: '#B71C1C', background: '#FFCDD2', borderColor: '#EF9A9A' },
};

// Paleta que o bloco de fotos espera (mesmas cores do papel compartilhado)
const PHOTO_PALETTE = {
  blueBg: '#F5F7FF',
  bluePale: '#E8EDFF',
  navy: '#1A2559',
  blue: '#5A80FB',
  white: '#FFFFFF',
  textLight: '#718096',
  textMedium: '#4A5568',
};

const SeverityBadge = ({ severity }) => {
  const style = SEVERITY_STYLE[severity] || { color: '#4A5568', background: '#FAFBFE', borderColor: '#E8ECF4' };
  return <span className={styles.badge} style={{ ...style, border: `1px solid ${style.borderColor}` }}>{severity}</span>;
};

const directionClass = (direction) => (direction === 'up' ? styles.up : direction === 'down' ? styles.down : styles.same);
const directionSign = (direction) => (direction === 'up' ? '+' : direction === 'down' ? '-' : '=');

/**
 * Miolo do PDF de assimetria craniana no papel compartilhado: resumo do
 * acompanhamento, medições atuais, classificação, evolução desde a primeira
 * avaliação, gráficos, histórico e registro fotográfico. Os números vêm do
 * mesmo adaptador do relatório clínico do card.
 *
 * `history`: medições do paciente; `photoReport`: saída de buildCranialPhotoReport
 * com as URLs já carregadas (ou null).
 */
export const CranialAsymmetryReportBody = ({ history, photoReport }) => {
  const list = Array.isArray(history) ? history : [];
  if (list.length === 0) return null;

  const current = buildCranialAsymmetryReportData(list, '');
  const summary = buildTreatmentSummary(list);
  const changes = buildMeasurementChanges(list);
  const rows = formatCranialAsymmetryHistory(list);
  const chartData = [...list].sort((a, b) => dayjs(a.createdAt).valueOf() - dayjs(b.createdAt).valueOf());

  return (
    <>
      {summary && (
        <div className={paper.section}>
          <span className={paper.sectionTitle}>Resumo do acompanhamento</span>
          <div className={styles.tiles}>
            <div className={styles.tile}><span className={styles.tileLabel}>Primeira avaliação</span><span className={styles.tileValue}>{summary.firstDate}</span></div>
            <div className={styles.tile}><span className={styles.tileLabel}>Última avaliação</span><span className={styles.tileValue}>{summary.lastDate}</span></div>
            <div className={styles.tile}><span className={styles.tileLabel}>Duração</span><span className={styles.tileValue}>{summary.duration}</span></div>
            <div className={styles.tile}><span className={styles.tileLabel}>Avaliações</span><span className={styles.tileValue}>{summary.totalAssessments}</span></div>
          </div>
        </div>
      )}

      <div className={paper.section}>
        <span className={paper.sectionTitle}>Medições atuais ({current.assessmentDate})</span>
        <div className={styles.measures}>
          {current.measurements.map((measure) => (
            <div key={measure.label} className={styles.tile}>
              <span className={styles.tileLabel}>{measure.label}</span>
              <span className={styles.tileValue}>
                {measure.value}
                {measure.severity && <SeverityBadge severity={measure.severity} />}
              </span>
              {measure.description && <span className={styles.tileHint}>{measure.description}</span>}
            </div>
          ))}
        </div>
      </div>

      <div className={paper.section}>
        <span className={paper.sectionTitle}>Classificação</span>
        <div className={styles.classification}>
          <span className={styles.classificationMain}>{current.classification}</span>
          <span className={styles.classificationSub}>De acordo com os índices clínicos da avaliação mais recente.</span>
        </div>
      </div>

      {changes && (
        <div className={paper.section}>
          <span className={paper.sectionTitle}>Evolução desde a primeira avaliação</span>
          <table className={paper.table}>
            <thead>
              <tr><th>Medida</th><th className={paper.num}>Inicial</th><th className={paper.num}>Atual</th><th className={paper.num}>Variação</th></tr>
            </thead>
            <tbody>
              {changes.map((change) => (
                <tr key={change.label}>
                  <td>{change.label}</td>
                  <td className={paper.num}>{change.initialValue}</td>
                  <td className={paper.num}>{change.currentValue}</td>
                  <td className={`${paper.num} ${styles.diff} ${directionClass(change.direction)}`}>{directionSign(change.direction)} {change.difference}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {list.length > 1 && (
        <div className={`${paper.section} ${styles.chart}`}>
          <span className={paper.sectionTitle}>Gráficos de evolução</span>
          <CranialAsymmetryChart data={chartData} />
        </div>
      )}

      <div className={paper.section}>
        <span className={paper.sectionTitle}>Histórico de avaliações</span>
        <table className={paper.table}>
          <thead>
            <tr>
              <th>Data</th>
              <th className={paper.num}>AP</th>
              <th className={paper.num}>BP</th>
              <th className={paper.num}>AP/BP %</th>
              <th>AP/BP</th>
              <th className={paper.num}>DD</th>
              <th className={paper.num}>DE</th>
              <th className={paper.num}>CVAI %</th>
              <th>Plagiocefalia</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={index}>
                <td>{row.date}</td>
                <td className={paper.num}>{row.ap}</td>
                <td className={paper.num}>{row.bp}</td>
                <td className={paper.num}>{row.apBpPercentage}</td>
                <td><SeverityBadge severity={row.apBpSeverity} /></td>
                <td className={paper.num}>{row.dd}</td>
                <td className={paper.num}>{row.de}</td>
                <td className={paper.num}>{row.plagioPercentage}</td>
                <td><SeverityBadge severity={row.plagioSeverity} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {photoReport && (
        <div className={paper.section}>
          <span className={paper.sectionTitle}>Registro fotográfico</span>
          <ClinicalReportPhotos photoReport={photoReport} palette={PHOTO_PALETTE} />
        </div>
      )}
    </>
  );
};

export default CranialAsymmetryReportBody;
